import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Container, Row, Col, Card, Button, Badge, Modal, Image, Form, Alert, ButtonGroup, Tabs, Tab, Table } from 'react-bootstrap';
import { fetchPedidosLimeira, fetchPedidoLimeiraDetalhe, atualizarStatusCompra } from '../../services/api';
import Loading from '../../components/Loading/Loading';
import SmartSearch from '../../components/SmartSearch';
import * as XLSX from 'xlsx';

const URGENCIA_CONFIG = {
    baixa: { label: 'Baixa', color: 'secondary', icon: '🟢' },
    normal: { label: 'Normal', color: 'info', icon: '🔵' },
    alta: { label: 'Alta', color: 'warning', icon: '🟡' },
    urgente: { label: 'Urgente', color: 'danger', icon: '🔴' }
};

// Configuração de status do fornecedor
const STATUS_FORNECEDOR = {
    aprovado: { label: 'Aprovados', color: 'success', icon: '✅', next: 'em_preparacao', btnLabel: '🔧 Iniciar Preparação' },
    em_preparacao: { label: 'Em Preparação', color: 'warning', icon: '🔧', next: 'pronto', btnLabel: '📦 Marcar como Pronto' },
    pronto: { label: 'Pronto', color: 'info', icon: '📦', next: 'enviado', btnLabel: '🚚 Marcar como Enviado' },
    enviado: { label: 'Enviados', color: 'primary', icon: '🚚', next: null, btnLabel: null }
};

// Configuração de status do pedido
const STATUS_PEDIDO = {
    aberto: { label: 'Aberto', color: 'warning', icon: '📂' },
    enviado: { label: 'Enviado', color: 'info', icon: '🚚' },
    recebido: { label: 'Recebido', color: 'success', icon: '✅' },
    cancelado: { label: 'Cancelado', color: 'danger', icon: '❌' }
};

const LimeiraFornecedor = () => {
    const queryClient = useQueryClient();
    const [pedidoSelecionadoId, setPedidoSelecionadoId] = useState(null);
    const [activeTab, setActiveTab] = useState('aprovado');
    const [showObsModal, setShowObsModal] = useState(false);
    const [obsContent, setObsContent] = useState('');
    const [alert, setAlert] = useState(null);

    // Estado para loja selecionada (divisão por origem)
    const [lojaSelecionada, setLojaSelecionada] = useState('todas');

    // Estado para busca inteligente (busca DENTRO de um pedido aberto)
    const [searchTerm, setSearchTerm] = useState('');

    // Estado para busca GLOBAL de produto (na grade de pedidos — descobre em qual pedido o produto está)
    const [globalSearch, setGlobalSearch] = useState('');
    const [globalSearchInput, setGlobalSearchInput] = useState('');

    // Tooltip de imagem do produto encontrado pela busca
    // Guarda { codigo, x, y } pra renderizar overlay flutuante posicionado próximo ao item.
    const [hoverImg, setHoverImg] = useState(null);

    // Estados para seleção múltipla
    const [modoSelecao, setModoSelecao] = useState(false);
    const [itensSelecionados, setItensSelecionados] = useState(new Set());


    // Query para buscar lista de pedidos — passa a busca global quando preenchida
    const { data: pedidosData, isLoading: isLoadingPedidos, refetch: refetchPedidos } = useQuery({
        queryKey: ['pedidos-limeira', globalSearch],
        queryFn: () => fetchPedidosLimeira(globalSearch ? { busca: globalSearch } : {}),
        refetchInterval: globalSearch ? false : 30000  // pausa o refetch automático durante busca pra não atrapalhar
    });

    // Debounce: aplica a busca 350ms depois que o usuário para de digitar
    useEffect(() => {
        const t = setTimeout(() => setGlobalSearch(globalSearchInput.trim()), 350);
        return () => clearTimeout(t);
    }, [globalSearchInput]);

    // Query para buscar detalhes do pedido selecionado
    const { data: pedidoDetalhe, isLoading: isLoadingDetalhe } = useQuery({
        queryKey: ['pedido-detalhe', pedidoSelecionadoId],
        queryFn: () => fetchPedidoLimeiraDetalhe(pedidoSelecionadoId),
        enabled: !!pedidoSelecionadoId,
        refetchInterval: 30000
    });

    // Mutation para atualizar status
    const atualizarMutation = useMutation({
        mutationFn: ({ id, status }) => atualizarStatusCompra(id, { status }),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['pedido-detalhe', pedidoSelecionadoId]);
            setAlert({ type: 'success', message: 'Status atualizado com sucesso!' });
            setTimeout(() => setAlert(null), 3000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao atualizar status' });
        }
    });

    // Mutation para atualizar múltiplos itens
    const atualizarMultiplosMutation = useMutation({
        mutationFn: async ({ ids, status }) => {
            const promises = ids.map(id => atualizarStatusCompra(id, { status }));
            return Promise.all(promises);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['pedido-detalhe', pedidoSelecionadoId]);
            setAlert({ type: 'success', message: `${data.length} itens atualizados com sucesso!` });
            setItensSelecionados(new Set());
            setModoSelecao(false);
            setTimeout(() => setAlert(null), 3000);
        },
        onError: (error) => {
            setAlert({ type: 'danger', message: error.message || 'Erro ao atualizar itens' });
        }
    });

    // Funções de seleção múltipla
    const toggleModoSelecao = () => {
        setModoSelecao(!modoSelecao);
        if (modoSelecao) {
            setItensSelecionados(new Set());
        }
    };

    const toggleSelecionarItem = (id) => {
        const novoSet = new Set(itensSelecionados);
        if (novoSet.has(id)) {
            novoSet.delete(id);
        } else {
            novoSet.add(id);
        }
        setItensSelecionados(novoSet);
    };

    const selecionarTodos = (itens) => {
        const novoSet = new Set(itens.map(i => i.id));
        setItensSelecionados(novoSet);
    };

    const deselecionarTodos = () => {
        setItensSelecionados(new Set());
    };

    const moverSelecionadosParaStatus = (novoStatus) => {
        const ids = Array.from(itensSelecionados);
        if (ids.length === 0) return;
        atualizarMultiplosMutation.mutate({ ids, status: novoStatus });
    };

    const pedidos = pedidosData?.data || [];
    const pedidoAtual = pedidoDetalhe?.data || null;
    const itensDoPedido = pedidoAtual?.itens || [];

    // Função auxiliar para normalizar origem da loja
    const normalizarOrigem = (origem) => {
        if (!origem) return 'ecommerce';
        const o = origem.toLowerCase();
        if (o.includes('centro')) return 'centro';
        if (o.includes('caxias')) return 'caxias';
        return 'ecommerce';
    };

    // Contadores por loja
    const contadoresPorLoja = useMemo(() => {
        const contador = { ecommerce: 0, centro: 0, caxias: 0 };
        itensDoPedido.forEach(item => {
            const loja = normalizarOrigem(item.origem);
            contador[loja]++;
        });
        return contador;
    }, [itensDoPedido]);

    // Filtrar itens pela loja selecionada
    const itensFiltradosPorLoja = useMemo(() => {
        let itens = itensDoPedido;

        // Filtrar por loja
        if (lojaSelecionada !== 'todas') {
            itens = itens.filter(item => normalizarOrigem(item.origem) === lojaSelecionada);
        }

        // Filtrar por termo de busca
        if (searchTerm && searchTerm.length >= 2) {
            const termLower = searchTerm.toLowerCase();
            // Remover acentos para busca fuzzy
            const termNormalized = termLower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

            itens = itens.filter(item => {
                const codigo = (item.produto_codigo || '').toLowerCase();
                const descricao = (item.produto_descricao || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const fornecedor = (item.fornecedor || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

                return codigo.includes(termLower) ||
                    descricao.includes(termNormalized) ||
                    fornecedor.includes(termNormalized);
            });
        }

        return itens;
    }, [itensDoPedido, lojaSelecionada, searchTerm]);

    // Filtrar itens por status
    const itensPorStatus = useMemo(() => ({
        aprovado: itensFiltradosPorLoja.filter(i => i.status === 'aprovado' || !i.status),
        em_preparacao: itensFiltradosPorLoja.filter(i => i.status === 'em_preparacao'),
        pronto: itensFiltradosPorLoja.filter(i => i.status === 'pronto'),
        enviado: itensFiltradosPorLoja.filter(i => i.status === 'enviado')
    }), [itensFiltradosPorLoja]);

    const formatCurrency = (value) => {
        if (!value) return 'R$ -';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const handleVerObservacao = (obs) => {
        setObsContent(obs);
        setShowObsModal(true);
    };

    const handleAtualizarStatus = (item, novoStatus) => {
        atualizarMutation.mutate({ id: item.id, status: novoStatus });
    };

    const getStatusConfig = (status) => {
        return STATUS_FORNECEDOR[status] || STATUS_FORNECEDOR.aprovado;
    };

    // Função para exportar pedido para Excel
    const exportarParaExcel = () => {
        const dadosExport = itensFiltradosPorLoja.map(item => ({
            'Código': item.produto_codigo || '',
            'Descrição': item.produto_descricao || '',
            'Fornecedor': item.fornecedor || '',
            'Origem': item.origem || 'E-commerce',
            'Quantidade': item.quantidade_solicitada || 0,
            'Custo Unitário': item.preco_custo || 0,
            'Valor Total': item.valor_total || 0,
            'Urgência': URGENCIA_CONFIG[item.urgencia]?.label || 'Normal',
            'Status': STATUS_FORNECEDOR[item.status || 'aprovado']?.label || 'Aprovado',
            'Data Aprovação': item.data_atualizacao ? new Date(item.data_atualizacao).toLocaleDateString('pt-BR') : '',
            'Observações': item.observacoes || ''
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(dadosExport);

        const colWidths = [
            { wch: 12 }, { wch: 40 }, { wch: 18 }, { wch: 12 },
            { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 10 },
            { wch: 14 }, { wch: 14 }, { wch: 30 }
        ];
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, 'Itens do Pedido');

        const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
        const nomeArquivo = `Pedido_${pedidoAtual?.numero_pedido || 'sem_numero'}_${dataAtual}.xlsx`;

        XLSX.writeFile(wb, nomeArquivo);

        setAlert({ type: 'success', message: `✅ Exportado ${dadosExport.length} itens para Excel!` });
        setTimeout(() => setAlert(null), 3000);
    };

    // Card do produto
    const renderProductCard = (item) => {
        const statusAtual = item.status || 'aprovado';
        const statusConfig = getStatusConfig(statusAtual);
        const proximoStatus = statusConfig.next;

        return (
            <Card key={item.id} className={`h-100 shadow-sm border-${statusConfig.color}`}>
                <div style={{ position: 'relative', paddingTop: '100%', background: '#f8f9fa' }}>
                    {item.produto_imagem ? (
                        <Image
                            src={item.produto_imagem}
                            style={{
                                position: 'absolute', top: 0, left: 0,
                                width: '100%', height: '100%', objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <div style={{
                            position: 'absolute', top: 0, left: 0,
                            width: '100%', height: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#adb5bd', fontSize: '2.5rem'
                        }}>📦</div>
                    )}

                    <Badge bg={URGENCIA_CONFIG[item.urgencia]?.color || 'secondary'}
                        style={{ position: 'absolute', top: '8px', right: '8px' }}>
                        {URGENCIA_CONFIG[item.urgencia]?.icon} {URGENCIA_CONFIG[item.urgencia]?.label}
                    </Badge>

                    <Badge bg="dark" style={{ position: 'absolute', top: '8px', left: '8px' }}>
                        Qtd: {item.quantidade_solicitada}
                    </Badge>


                </div>

                <Card.Body className="p-2">
                    <small className="text-muted">#{item.produto_codigo}</small>
                    <div className="fw-bold text-truncate" title={item.produto_descricao} style={{ fontSize: '0.85rem' }}>
                        {item.produto_descricao}
                    </div>
                    <div className="text-primary fw-bold">Custo: {formatCurrency(item.preco_custo)}</div>
                    <div className="d-flex justify-content-between mt-1">
                        <small className="text-muted">{item.fornecedor || 'N/A'}</small>
                        <small className="text-success fw-bold">{formatCurrency(item.valor_total)}</small>
                    </div>
                </Card.Body>

                <Card.Footer className="p-2 bg-white">
                    <div className="d-flex flex-column gap-1">
                        {item.observacoes && (
                            <Button variant="outline-danger" size="sm"
                                onClick={() => handleVerObservacao(item.observacoes)}
                                className="w-100" style={{ fontSize: '0.75rem' }}>
                                📝 Ver Observação
                            </Button>
                        )}

                        {proximoStatus && (
                            <Button variant={STATUS_FORNECEDOR[proximoStatus].color} size="sm"
                                onClick={() => handleAtualizarStatus(item, proximoStatus)}
                                disabled={atualizarMutation.isPending}
                                className="w-100" style={{ fontSize: '0.75rem' }}>
                                {statusConfig.btnLabel}
                            </Button>
                        )}

                        {statusAtual === 'enviado' && (
                            <div className="text-center text-primary fw-bold small py-1">
                                🚚 Pedido Enviado!
                            </div>
                        )}
                    </div>
                </Card.Footer>
            </Card>
        );
    };

    // Grid de produtos
    const renderGrid = (itens, emptyMessage, selecaoAtiva = false) => {
        if (itens.length === 0) {
            return (
                <div className="text-center text-muted py-5">
                    <div style={{ fontSize: '4rem' }}>📭</div>
                    <h5 className="mt-3">{emptyMessage}</h5>
                </div>
            );
        }

        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(195px, 1fr))', gap: '0.75rem' }}>
                {itens.map(item => {
                    const estaSelecionado = itensSelecionados.has(item.id);
                    return (
                        <div
                            key={item.id}
                            onClick={selecaoAtiva ? () => toggleSelecionarItem(item.id) : undefined}
                            style={{
                                cursor: selecaoAtiva ? 'pointer' : 'default',
                                position: 'relative'
                            }}
                        >
                            {selecaoAtiva && (
                                <div style={{
                                    position: 'absolute',
                                    top: '5px',
                                    left: '5px',
                                    zIndex: 10,
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    background: estaSelecionado ? '#0d6efd' : 'rgba(255,255,255,0.9)',
                                    border: estaSelecionado ? '2px solid #0d6efd' : '2px solid #6c757d',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }}>
                                    {estaSelecionado && '✓'}
                                </div>
                            )}
                            <div style={{
                                border: estaSelecionado ? '3px solid #0d6efd' : 'none',
                                borderRadius: '8px',
                                opacity: selecaoAtiva && !estaSelecionado ? 0.7 : 1,
                                transition: 'all 0.2s'
                            }}>
                                {renderProductCard(item)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ========================================
    // RENDER: Lista de Pedidos
    // ========================================
    if (!pedidoSelecionadoId) {
        return (
            <Container fluid className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h1 className="h3 mb-0 text-gray-800">📦 Pedidos - Fornecedor</h1>
                        <p className="mb-0 text-muted">
                            {globalSearch
                                ? `Mostrando pedidos que contêm "${globalSearch}"`
                                : 'Selecione um pedido para visualizar os itens'}
                        </p>
                    </div>
                    <Button variant="outline-secondary" onClick={() => refetchPedidos()}>
                        🔄 Atualizar
                    </Button>
                </div>

                {/* Busca global por produto em qualquer pedido */}
                <div className="mb-4">
                    <div className="position-relative" style={{ maxWidth: 600 }}>
                        <input
                            type="text"
                            className="form-control form-control-lg"
                            placeholder="🔍 Buscar produto (código ou descrição) em qualquer pedido..."
                            value={globalSearchInput}
                            onChange={(e) => setGlobalSearchInput(e.target.value)}
                            style={{ paddingRight: globalSearchInput ? 100 : 16 }}
                        />
                        {globalSearchInput && (
                            <button
                                onClick={() => { setGlobalSearchInput(''); setGlobalSearch(''); }}
                                className="btn btn-sm btn-link position-absolute"
                                style={{ right: 8, top: '50%', transform: 'translateY(-50%)', textDecoration: 'none' }}
                                title="Limpar busca"
                            >
                                ✕ Limpar
                            </button>
                        )}
                    </div>
                    {globalSearch && !isLoadingPedidos && (
                        <small className="text-muted ms-1">
                            {pedidos.length === 0
                                ? 'Nenhum pedido contém esse produto.'
                                : `${pedidos.length} pedido(s) com matches.`}
                        </small>
                    )}
                </div>

                {isLoadingPedidos && <Loading />}

                {!isLoadingPedidos && pedidos.length === 0 && (
                    <div className="text-center py-5">
                        <div style={{ fontSize: '6rem' }}>{globalSearch ? '🔍' : '📭'}</div>
                        <h4 className="mt-3">
                            {globalSearch
                                ? `Nenhum pedido contém "${globalSearch}"`
                                : 'Nenhum pedido encontrado'}
                        </h4>
                        <p className="text-muted">
                            {globalSearch
                                ? 'Tente outro código ou descrição.'
                                : 'Quando pedidos forem criados, eles aparecerão aqui.'}
                        </p>
                    </div>
                )}

                {/* Tooltip flutuante com foto do produto em hover sobre item encontrado */}
                {hoverImg && (
                    <div
                        style={{
                            position: 'fixed',
                            left: hoverImg.x,
                            top: hoverImg.y,
                            zIndex: 9999,
                            background: '#fff',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                            padding: 6,
                            pointerEvents: 'none',
                            width: 180
                        }}
                    >
                        <img
                            src={`/api/produtos/imagem/${encodeURIComponent(hoverImg.codigo)}`}
                            alt={hoverImg.codigo}
                            style={{ width: '100%', height: 168, objectFit: 'contain', background: '#f9fafb', borderRadius: 4 }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#111827', marginTop: 4 }}>
                            {hoverImg.codigo}
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#6b7280', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {hoverImg.descricao}
                        </div>
                    </div>
                )}

                {!isLoadingPedidos && pedidos.length > 0 && (
                    <Row xs={1} md={2} lg={3} xl={4} className="g-4">
                        {pedidos.map(pedido => {
                            const statusConfig = STATUS_PEDIDO[pedido.status] || STATUS_PEDIDO.aberto;
                            return (
                                <Col key={pedido.id}>
                                    <Card
                                        className={`h-100 shadow-sm border-${statusConfig.color} cursor-pointer`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setPedidoSelecionadoId(pedido.id)}
                                    >
                                        <Card.Header className={`bg-${statusConfig.color} bg-opacity-10`}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <strong>{statusConfig.icon} {pedido.numero_pedido}</strong>
                                                <Badge bg={statusConfig.color}>{statusConfig.label}</Badge>
                                            </div>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Itens:</span>
                                                <strong>{pedido.total_itens}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Valor Total:</span>
                                                <strong className="text-success">{formatCurrency(pedido.valor_total)}</strong>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Criado em:</span>
                                                <span>{formatDate(pedido.data_criacao)}</span>
                                            </div>
                                            {pedido.usuario_criador_nome && (
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">Por:</span>
                                                    <span>{pedido.usuario_criador_nome}</span>
                                                </div>
                                            )}

                                            {/* Itens encontrados pela busca global — destaque amarelo */}
                                            {pedido.itens_match && pedido.itens_match.length > 0 && (
                                                <div
                                                    className="mt-3 p-2"
                                                    style={{
                                                        background: '#fff8e1',
                                                        border: '1px solid #ffd54f',
                                                        borderRadius: 6
                                                    }}
                                                >
                                                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7a5a00', letterSpacing: '0.5px', marginBottom: 6 }}>
                                                        🎯 {pedido.itens_match.length} ITEM(NS) ENCONTRADO(S)
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        {pedido.itens_match.slice(0, 5).map((it, idx) => (
                                                            <div
                                                                key={idx}
                                                                style={{ fontSize: '0.78rem', lineHeight: 1.3, cursor: 'help' }}
                                                                onMouseEnter={(e) => {
                                                                    const r = e.currentTarget.getBoundingClientRect();
                                                                    setHoverImg({
                                                                        codigo: it.produto_codigo,
                                                                        descricao: it.produto_descricao,
                                                                        x: r.right + 8,
                                                                        y: r.top
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverImg(null)}
                                                            >
                                                                <div style={{ fontWeight: 600, color: '#1f2937' }}>
                                                                    {it.produto_codigo} <span style={{ color: '#15803d', marginLeft: 4 }}>× {it.quantidade_solicitada}</span>
                                                                </div>
                                                                <div style={{ color: '#475569', fontSize: '0.72rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                                    {it.produto_descricao}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {pedido.itens_match.length > 5 && (
                                                            <div style={{ fontSize: '0.7rem', color: '#7a5a00', fontStyle: 'italic' }}>
                                                                +{pedido.itens_match.length - 5} mais — clique no card pra ver
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                        <Card.Footer className="bg-white text-center">
                                            <small className="text-primary">👆 Clique para ver itens</small>
                                        </Card.Footer>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Container>
        );
    }

    // ========================================
    // RENDER: Detalhes do Pedido (itens)
    // ========================================
    return (
        <Container fluid className="p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => { setPedidoSelecionadoId(null); setLojaSelecionada('todas'); }}
                        className="mb-2"
                    >
                        ← Voltar aos Pedidos
                    </Button>
                    <h1 className="h3 mb-0 text-gray-800">
                        📦 {pedidoAtual?.numero_pedido || 'Pedido'}
                    </h1>
                    <p className="mb-0 text-muted">
                        {pedidoAtual?.total_itens} itens • {formatCurrency(pedidoAtual?.valor_total)}
                    </p>
                </div>
                <div className="d-flex gap-2 align-items-end">
                    <div style={{ minWidth: '300px' }}>
                        <SmartSearch
                            placeholder="Buscar produto no pedido..."
                            searchEndpoint="/api/search/suggestions?tipo=loja-fisica"
                            storageKey="limeira-fornecedor-search"
                            onSearch={(term) => setSearchTerm(term)}
                            onSelect={(item) => {
                                if (item && item.codigo) {
                                    setSearchTerm(item.descricao || item.codigo);
                                }
                            }}
                        />
                    </div>
                    <Button
                        variant="success"
                        onClick={exportarParaExcel}
                        disabled={itensFiltradosPorLoja.length === 0}
                    >
                        📊 Exportar Excel ({itensFiltradosPorLoja.length})
                    </Button>
                </div>
            </div>

            {/* Alert */}
            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mb-4">
                    {alert.message}
                </Alert>
            )}

            {/* Filtro por Loja */}
            <Card className="shadow mb-4">
                <Card.Header className="py-3">
                    <h6 className="m-0 font-weight-bold text-primary">🏪 Filtrar por Loja</h6>
                </Card.Header>
                <Card.Body>
                    <ButtonGroup className="w-100">
                        <Button
                            variant={lojaSelecionada === 'todas' ? 'dark' : 'outline-dark'}
                            onClick={() => setLojaSelecionada('todas')}
                        >
                            📊 Todas <Badge bg="secondary" className="ms-1">{itensDoPedido.length}</Badge>
                        </Button>
                        <Button
                            variant={lojaSelecionada === 'ecommerce' ? 'primary' : 'outline-primary'}
                            onClick={() => setLojaSelecionada('ecommerce')}
                        >
                            🛒 E-commerce <Badge bg="light" text="dark" className="ms-1">{contadoresPorLoja.ecommerce}</Badge>
                        </Button>
                        <Button
                            variant={lojaSelecionada === 'centro' ? 'warning' : 'outline-warning'}
                            onClick={() => setLojaSelecionada('centro')}
                            className={lojaSelecionada === 'centro' ? 'text-dark' : ''}
                        >
                            🏪 Centro <Badge bg="light" text="dark" className="ms-1">{contadoresPorLoja.centro}</Badge>
                        </Button>
                        <Button
                            variant={lojaSelecionada === 'caxias' ? 'info' : 'outline-info'}
                            onClick={() => setLojaSelecionada('caxias')}
                        >
                            📍 Caxias <Badge bg="light" text="dark" className="ms-1">{contadoresPorLoja.caxias}</Badge>
                        </Button>
                    </ButtonGroup>
                </Card.Body>
            </Card>

            {/* Stats */}
            <Row className="mb-4 g-3">
                <Col md={3}>
                    <Card className={`border-success border-2 ${activeTab === 'aprovado' ? 'bg-success bg-opacity-10' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab('aprovado')}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle bg-success text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>✅</div>
                            <div>
                                <h5 className="mb-0">{itensPorStatus.aprovado.length}</h5>
                                <small className="text-muted">Aprovados</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className={`border-warning border-2 ${activeTab === 'em_preparacao' ? 'bg-warning bg-opacity-10' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab('em_preparacao')}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle bg-warning text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>🔧</div>
                            <div>
                                <h5 className="mb-0">{itensPorStatus.em_preparacao.length}</h5>
                                <small className="text-muted">Em Preparação</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className={`border-info border-2 ${activeTab === 'pronto' ? 'bg-info bg-opacity-10' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab('pronto')}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle bg-info text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>📦</div>
                            <div>
                                <h5 className="mb-0">{itensPorStatus.pronto.length}</h5>
                                <small className="text-muted">Pronto</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className={`border-primary border-2 ${activeTab === 'enviado' ? 'bg-primary bg-opacity-10' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveTab('enviado')}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="rounded-circle bg-primary text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>🚚</div>
                            <div>
                                <h5 className="mb-0">{itensPorStatus.enviado.length}</h5>
                                <small className="text-muted">Enviados</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Loading */}
            {isLoadingDetalhe && <Loading />}

            {/* Tabs de Status */}
            {!isLoadingDetalhe && (
                <Card className="shadow">
                    <Card.Body>
                        <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k); setModoSelecao(false); setItensSelecionados(new Set()); }} className="mb-4" fill>
                            <Tab eventKey="aprovado"
                                title={<span>✅ Aprovados <Badge bg="success" className="ms-2">{itensPorStatus.aprovado.length}</Badge></span>}>
                                <div className="p-3" style={{ background: '#e8f5e9', borderRadius: '8px', minHeight: '300px' }}>
                                    {/* Header com botões de ação */}
                                    {itensPorStatus.aprovado.length > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-white rounded">
                                            <span className="fw-bold text-success">
                                                {modoSelecao && itensSelecionados.size > 0
                                                    ? `${itensSelecionados.size} selecionado(s)`
                                                    : `${itensPorStatus.aprovado.length} itens`}
                                            </span>
                                            <div className="d-flex gap-2">
                                                {!modoSelecao ? (
                                                    <Button variant="warning" size="sm" onClick={toggleModoSelecao}>
                                                        ☑️ Selecionar
                                                    </Button>
                                                ) : (
                                                    <>
                                                        <Button variant="outline-secondary" size="sm" onClick={() => selecionarTodos(itensPorStatus.aprovado)}>
                                                            Todos
                                                        </Button>
                                                        <Button variant="outline-secondary" size="sm" onClick={deselecionarTodos}>
                                                            Limpar
                                                        </Button>
                                                        <Button
                                                            variant="warning"
                                                            size="sm"
                                                            disabled={itensSelecionados.size === 0 || atualizarMultiplosMutation.isPending}
                                                            onClick={() => moverSelecionadosParaStatus('em_preparacao')}
                                                        >
                                                            🔧 Iniciar Preparação ({itensSelecionados.size})
                                                        </Button>
                                                        <Button variant="secondary" size="sm" onClick={toggleModoSelecao}>
                                                            Cancelar
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {renderGrid(itensPorStatus.aprovado, 'Nenhum item aguardando preparação', modoSelecao)}
                                </div>
                            </Tab>

                            <Tab eventKey="em_preparacao"
                                title={<span>🔧 Em Preparação <Badge bg="warning" className="ms-2">{itensPorStatus.em_preparacao.length}</Badge></span>}>
                                <div className="p-3" style={{ background: '#fff8e1', borderRadius: '8px', minHeight: '300px' }}>
                                    {/* Header com botões de ação */}
                                    {itensPorStatus.em_preparacao.length > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-white rounded">
                                            <span className="fw-bold text-warning">
                                                {modoSelecao && itensSelecionados.size > 0
                                                    ? `${itensSelecionados.size} selecionado(s)`
                                                    : `${itensPorStatus.em_preparacao.length} itens`}
                                            </span>
                                            <div className="d-flex gap-2">
                                                {!modoSelecao ? (
                                                    <Button variant="info" size="sm" onClick={toggleModoSelecao}>
                                                        ☑️ Selecionar
                                                    </Button>
                                                ) : (
                                                    <>
                                                        <Button variant="outline-secondary" size="sm" onClick={() => selecionarTodos(itensPorStatus.em_preparacao)}>
                                                            Todos
                                                        </Button>
                                                        <Button variant="outline-secondary" size="sm" onClick={deselecionarTodos}>
                                                            Limpar
                                                        </Button>
                                                        <Button
                                                            variant="info"
                                                            size="sm"
                                                            disabled={itensSelecionados.size === 0 || atualizarMultiplosMutation.isPending}
                                                            onClick={() => moverSelecionadosParaStatus('pronto')}
                                                        >
                                                            📦 Marcar Pronto ({itensSelecionados.size})
                                                        </Button>
                                                        <Button variant="secondary" size="sm" onClick={toggleModoSelecao}>
                                                            Cancelar
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {renderGrid(itensPorStatus.em_preparacao, 'Nenhum item em preparação', modoSelecao)}
                                </div>
                            </Tab>

                            <Tab eventKey="pronto"
                                title={<span>📦 Pronto <Badge bg="info" className="ms-2">{itensPorStatus.pronto.length}</Badge></span>}>
                                <div className="p-3" style={{ background: '#e3f2fd', borderRadius: '8px', minHeight: '300px' }}>
                                    {/* Header com botões de ação */}
                                    {itensPorStatus.pronto.length > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-white rounded">
                                            <span className="fw-bold text-info">
                                                {modoSelecao && itensSelecionados.size > 0
                                                    ? `${itensSelecionados.size} selecionado(s)`
                                                    : `${itensPorStatus.pronto.length} itens`}
                                            </span>
                                            <div className="d-flex gap-2">
                                                {!modoSelecao ? (
                                                    <Button variant="primary" size="sm" onClick={toggleModoSelecao}>
                                                        ☑️ Selecionar
                                                    </Button>
                                                ) : (
                                                    <>
                                                        <Button variant="outline-secondary" size="sm" onClick={() => selecionarTodos(itensPorStatus.pronto)}>
                                                            Todos
                                                        </Button>
                                                        <Button variant="outline-secondary" size="sm" onClick={deselecionarTodos}>
                                                            Limpar
                                                        </Button>
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            disabled={itensSelecionados.size === 0 || atualizarMultiplosMutation.isPending}
                                                            onClick={() => moverSelecionadosParaStatus('enviado')}
                                                        >
                                                            🚚 Marcar Enviado ({itensSelecionados.size})
                                                        </Button>
                                                        <Button variant="secondary" size="sm" onClick={toggleModoSelecao}>
                                                            Cancelar
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {renderGrid(itensPorStatus.pronto, 'Nenhum item pronto para envio', modoSelecao)}
                                </div>
                            </Tab>

                            <Tab eventKey="enviado"
                                title={<span>🚚 Enviados <Badge bg="primary" className="ms-2">{itensPorStatus.enviado.length}</Badge></span>}>
                                <div className="p-3" style={{ background: '#e8eaf6', borderRadius: '8px', minHeight: '300px' }}>
                                    {renderGrid(itensPorStatus.enviado, 'Nenhum item enviado', false)}
                                </div>
                            </Tab>
                        </Tabs>
                    </Card.Body>
                </Card>
            )}

            {/* Modal de Observação */}
            <Modal show={showObsModal} onHide={() => setShowObsModal(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="h5">📝 Observação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">{obsContent || 'Nenhuma observação.'}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowObsModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LimeiraFornecedor;
